import { useState, useMemo, useCallback } from 'react';
import {
  useGetList,
  useTranslate,
  useNotify,
  useDataProvider,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  CircularProgress,
  InputAdornment,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import ShortcutIcon from '@mui/icons-material/Shortcut';
import { formatPhoneForDisplay } from '../../utils/phone';
import type { Message, ChannelType, ContactIdentity, Conversation } from '../../types/chat.types';

interface ForwardMessageDialogProps {
  open: boolean;
  onClose: () => void;
  message: Message | null;
  channelConnectionId: number | null;
  channelType: ChannelType | null;
  onSuccess?: (conversationId: number) => void;
}

/**
 * ForwardMessageDialog - Contact picker for forwarding messages
 *
 * Shows contacts from the same channel connection.
 * User picks a contact → system finds/creates conversation → forwards message.
 *
 * Flow:
 * 1. Search contacts (contact-identity) on the same channel connection
 * 2. User selects a contact
 * 3. Find existing conversation for that contact + channel connection
 * 4. Call POST /message/forward with messageId + targetConversationId
 * 5. Navigate to target conversation on success
 */
export function ForwardMessageDialog({
  open,
  onClose,
  message,
  channelConnectionId,
  channelType,
  onSuccess,
}: ForwardMessageDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [searchText, setSearchText] = useState('');
  const [selectedIdentity, setSelectedIdentity] = useState<ContactIdentity | null>(null);
  const [isForwarding, setIsForwarding] = useState(false);

  // Search contact identities on the same channel connection
  const { data: identities, isLoading: isLoadingIdentities } = useGetList<ContactIdentity>(
    'contact-identity',
    {
      pagination: { page: 1, perPage: 50 },
      filter: channelConnectionId
        ? {
            channelConnectionId_eq: channelConnectionId,
            ...(searchText.length >= 2 ? { name_q: searchText } : {}),
          }
        : {},
      sort: { field: 'name', order: 'ASC' },
    },
    { enabled: open && !!channelConnectionId },
  );

  // Filter out the current conversation's contact to avoid forwarding to self
  const filteredIdentities = useMemo(() => {
    if (!identities) return [];
    return identities;
  }, [identities]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setSelectedIdentity(null);
  }, []);

  const handleSelectIdentity = useCallback((identity: ContactIdentity) => {
    setSelectedIdentity(identity);
  }, []);

  const handleClose = useCallback(() => {
    setSearchText('');
    setSelectedIdentity(null);
    setIsForwarding(false);
    onClose();
  }, [onClose]);

  const handleForward = useCallback(async () => {
    if (!message || !selectedIdentity) return;

    setIsForwarding(true);

    try {
      // Find existing conversation for this contact + channel connection
      const conversationsResult = await dataProvider.getList<Conversation>('conversation', {
        pagination: { page: 1, perPage: 1 },
        filter: {
          contactId_eq: selectedIdentity.contactId,
          channelConnectionId_eq: selectedIdentity.channelConnectionId,
        },
        sort: { field: 'lastActivityAt', order: 'DESC' },
      });

      const targetConversation = conversationsResult?.data?.[0];

      if (!targetConversation) {
        notify(translate('resources.chat.forward.noConversation'), { type: 'error' });
        setIsForwarding(false);
        return;
      }

      // Forward the message
      await (dataProvider as any).messageForward({
        messageId: message.id as number,
        targetConversationId: targetConversation.id as number,
      });

      notify(translate('resources.chat.forward.success'), { type: 'success' });
      handleClose();

      // Navigate to target conversation
      if (onSuccess) {
        onSuccess(targetConversation.id as number);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'ra.notification.http_error';
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsForwarding(false);
    }
  }, [message, selectedIdentity, dataProvider, notify, translate, handleClose, onSuccess]);

  const getContactDisplayName = (identity: ContactIdentity) => {
    return identity.name || identity.contact?.name || identity.externalId;
  };

  const getContactSubtitle = (identity: ContactIdentity) => {
    if (identity.isGroup) {
      return translate('resources.chat.forward.group');
    }
    if (identity.contact?.phoneNumber) {
      return formatPhoneForDisplay(identity.contact.phoneNumber).international;
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <ShortcutIcon color="primary" />
            {translate('resources.chat.forward.title')}
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Message preview */}
        {message && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: 'action.hover',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              {translate('resources.chat.forward.forwarding')}
            </Typography>
            <Box
              sx={{
                pl: 1.5,
                borderLeft: 3,
                borderLeftColor: 'primary.main',
              }}
            >
              <Typography variant="body2" noWrap>
                {message.body || `[${translate('resources.chat.attachment')}]`}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Search input */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={translate('resources.chat.forward.searchContact')}
            value={searchText}
            onChange={handleSearchChange}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Selected contact chip */}
        {selectedIdentity && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Chip
              icon={selectedIdentity.isGroup ? <GroupIcon /> : <PersonIcon />}
              label={
                getContactSubtitle(selectedIdentity)
                  ? `${getContactDisplayName(selectedIdentity)} · ${getContactSubtitle(selectedIdentity)}`
                  : getContactDisplayName(selectedIdentity)
              }
              onDelete={() => setSelectedIdentity(null)}
              color="primary"
              variant="outlined"
            />
          </Box>
        )}

        {/* Contact list */}
        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          {isLoadingIdentities ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredIdentities.length === 0 ? (
            <Box py={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                {searchText.length >= 2
                  ? translate('resources.chat.forward.noResults')
                  : translate('resources.chat.forward.typeToSearch')}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredIdentities.map((identity) => {
                const isSelected = selectedIdentity?.id === identity.id;
                const displayName = getContactDisplayName(identity);
                const subtitle = getContactSubtitle(identity);

                return (
                  <ListItemButton
                    key={identity.id}
                    selected={isSelected}
                    onClick={() => handleSelectIdentity(identity)}
                    sx={{
                      px: 2,
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        '&:hover': { bgcolor: 'primary.light' },
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={identity.avatarUrl || undefined}
                        sx={{ width: 36, height: 36 }}
                      >
                        {identity.isGroup ? <GroupIcon /> : <PersonIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={displayName}
                      secondary={subtitle}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected ? 600 : 400 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={handleClose} color="inherit">
          {translate('ra.action.cancel')}
        </Button>
        <Button
          onClick={handleForward}
          variant="contained"
          disabled={!selectedIdentity || isForwarding}
          startIcon={isForwarding ? <CircularProgress size={16} /> : <ShortcutIcon />}
        >
          {translate('resources.chat.forward.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ForwardMessageDialog;
